import { messages } from "../../../src/resources/strings";
import { useAppDispatch, useAppSelector } from "../../store/Store";
import { handleInvalidTokenCase } from "../../utilities/AuthHelper";
import CheckSession from "../../utilities/CheckSession";
import { useAuth0 } from "@auth0/auth0-react";
import { setError, setLoading } from "../../store/slices/PolicyDetailSlice";
import { introspectionAPI } from "../user";
import { getDocumentUrls, getPDF } from "../docs";
import { DocumentItem, toDocumentsList } from "../../hooks/docs/DocumentParser";
import { Platform } from "react-native";
import { NavigationScreenName } from "../../utilities/Constants";
import { documentNameFormatter } from "../../utilities/Formatters";

const documentHandler = ({
  setItemPressedFlag,
  setPdfNavigationFlag,
  setDocumentsArray,
  navigation,
}: {
  setItemPressedFlag: (data: boolean) => void;
  setPdfNavigationFlag: (data: boolean) => void;
  setDocumentsArray: (data: DocumentItem[]) => void;
  navigation: any;
}) => {
  const { isConnected } = useAppSelector((state) => state.userReducer);
  const { logout } = useAuth0();

  const dispatch = useAppDispatch();
  const { policyNumber } = useAppSelector((state) => state.policyReducer);
  const { policyNumber: detailedPolicy } = useAppSelector(
    (state) => state.policyDetailReducer,
  );

  // Session Management
  const fetchDocs = async () => {
    const session = await CheckSession();
    if (session) {
      callGetDocumentUrlsAPI(policyNumber);
    } else {
      handleInvalidToken();
    }
  };
  const handleInvalidToken = () => {
    handleInvalidTokenCase(
      isConnected ?? false,
      navigation,
      logout,
      showInternetalert,
    );
  };

  // network check
  const checkNetworkAndProceed = async (callback: () => Promise<void>) => {
    if (isConnected) {
      await callback();
    } else {
      await showInternetalert();
    }
  };
  const showInternetalert = async () => {
    dispatch(setError(messages.error.noError));
    setTimeout(() => {
      dispatch(setError(messages.error.youAreOffline));
    }, 10);
  };

  // docs apis
  const callGetDocumentUrlsAPI = async (policyNumber: string) => {
    checkNetworkAndProceed(async () => {
      try {
        dispatch(setLoading(true));
        const introspectionResponse = await introspectionAPI();
        if (introspectionResponse.response) {
          const responseObj: any = await getDocumentUrls(policyNumber);
          if (responseObj.errorCode == messages.error.noError) {
            setDocumentsArray(toDocumentsList(responseObj.response));
            dispatch(setError(messages.error.noError));
          } else if (responseObj.errorCode == messages.error.callAgainAPI) {
            dispatch(setError(responseObj.errorCode));
            callGetDocumentUrlsAPI(policyNumber);
          }
        } else {
          dispatch(setError(messages.error.resetPasswordLogout));
        }
      } catch (error: any) {
        console.error("Error fetching document URLs:", error);
        if (!detailedPolicy) {
          dispatch(setError(messages.error.noDataFound));
        }
        dispatch(setError(error?.toString() || messages.error.somthingWrong));
      } finally {
        dispatch(setLoading(false));
      }
    });
  };
  const callIntrospectionAndGetPdfApi = async (document: DocumentItem) => {
    dispatch(setLoading(true));
    checkNetworkAndProceed(async () => {
      const introspectionResponse = await introspectionAPI();
      if (introspectionResponse.response) {
        if (Platform.OS == "web") {
          callGetPdfAPI(document);
        } else {
          setItemPressedFlag(true);
          setPdfNavigationFlag(true);
          dispatch(setLoading(false));
          navigation.navigate(NavigationScreenName.pdfViewer, { document });
        }
      } else {
        dispatch(setLoading(false));
        dispatch(setError(messages.error.resetPasswordLogout));
      }
      // dispatch(setLoading(false));
    });
  };
  const callGetPdfAPI = async (document: DocumentItem) => {
    checkNetworkAndProceed(async () => {
      const responseObj: any = await getPDF(document);
      if (!responseObj) {
        dispatch(setError(messages.error.documentDownloadFailed));
        return;
      }
      if (responseObj.errorCode == messages.error.noError) {
        // opening the pdf in new window
        window.open(responseObj.response);
        dispatch(setLoading(false));
      } else {
        if (
          Platform.OS === "web" &&
          responseObj == messages.error.documentDownloadFailed
        ) {
          dispatch(
            setError(
              `${messages.error.pdfError}: ${documentNameFormatter(
                document?.fileName,
              )}`,
            ),
          );
        } else {
          dispatch(setError(responseObj.errorCode));
        }
      }
    });
  };

  return {
    fetchDocs,
    callIntrospectionAndGetPdfApi,
    callGetDocumentUrlsAPI,
  };
};

export default documentHandler;
